import { computed } from "vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import Tinycolor from "tinycolor2";

import { editorAreas } from "./editorAreas";
import { canAreaBeDisplayed } from "./project.store";
import { cloneDeep } from "lodash-es";
import { getContrastColor } from "@/helpers/utility/Color";

import type { WritableComputedRef } from "vue";
import type { StateTree, Store } from "pinia";
import type { EditorAreaName } from "./editorAreas";
import type { RoundUnit } from "@editor/items/simulationRound/SimulationRoundItem";

export type ColorName =
  | "colorPrimary"
  | "colorPrimaryContrast"
  | "colorPrimaryTransparent"
  | "colorPrimaryLight"
  | "colorPrimaryDark"
  | "colorGrey"
  | "colorGreyDark"
  | "colorGreyDarker"
  | "colorGreyLight"
  | "colorGreyLighter"
  | "colorRelationStandard"
  | "colorRelationFaded"
  | "colorRelationActive"
  | "colorRelationHighlighted"
  | "colorRelationIncoming"
  | "colorRelationOutgoing"
  | "colorRelationConnecting"
  | "colorRelationInverse"
  | "colorRelationInverseActive"
  | "colorRelationInverseIncoming"
  | "colorRelationInverseOutgoing"
  | "colorRelationInverseConnecting";

type ColorHueName =
  | "colorPrimaryHue"
  | "colorRelationActiveHue"
  | "colorRelationInverseActiveHue";

const STANDARD_COLOR_AMOUNT = 5;

export interface ModelSettings {
  simulationRoundName: string | null;
  simulationRoundUnit: RoundUnit;
  simulationRoundLength: number;
  simulationRoundCustomized: boolean;
  simulationDurationType: "roundAmount" | "timeframe";
  simulationRoundAmount: number;
  simulationTimeStart: string | null;
  simulationTimeEnd: string | null;

  targetValueColors: string[];
  customTargetValueAreaColors: boolean;
  developerMode: boolean;

  relationStrengthPresets: {
    weak: number;
    medium: number;
    strong: number;
  };
  areaOrder: EditorAreaName[];
  hiddenAreas: EditorAreaName[];

  netGridSize: number;

  netActionsBarExtended: boolean;

  // colors
  colorPrimary: string | null;
  colorPrimaryContrast: string | null; // auto-generated
  colorPrimaryHue: number | null;
  colorPrimaryTransparent: string | null; // auto-generated
  colorPrimaryLight: string | null; // auto-generated
  colorPrimaryDark: string | null; // auto-generated

  colorGrey: string | null; // auto-generated
  colorGreyDark: string | null; // auto-generated
  colorGreyDarker: string | null; // auto-generated
  colorGreyLight: string | null; // auto-generated
  colorGreyLighter: string | null; // auto-generated

  colorRelationStandard: string | null;
  colorRelationFaded: string | null;
  colorRelationActive: string | null;
  colorRelationActiveHue: number | null;
  colorRelationHighlighted: string | null;
  colorRelationIncoming: string | null;
  colorRelationOutgoing: string | null;
  colorRelationConnecting: string | null;

  extraInverseRelationColors: boolean;
  colorRelationInverse: string | null;
  colorRelationInverseActive: string | null;
  colorRelationInverseActiveHue: number | null;
  colorRelationInverseIncoming: string | null;
  colorRelationInverseOutgoing: string | null;
  colorRelationInverseConnecting: string | null;

  resourceItemsPosition: "net" | "bar"; // ('net' | 'bar')

  netScaleBarVisible: boolean;
  netScaleIconsVisible: boolean;

  darkModeOverride: boolean | null; // deactivate/activate dark mode in this model, regardless of the global setting
}

const DEFAULT_ELEMENT_VALUE_COLOR_GOOD = "rgb(30, 221, 72)";
const DEFAULT_ELEMENT_VALUE_COLOR_BAD = "rgb(255, 62, 4)";
const DEFAULT_ELEMENT_VALUE_COLOR_NEUTRAL = "rgb(255, 255, 0)";

export const defaultSettings: ModelSettings = {
  // simulation round settings
  simulationRoundName: null, // deprecated.
  simulationRoundUnit: "round",
  simulationRoundLength: 1, // how many round units does a round use (e.g. 5 years = 1 round)
  simulationRoundCustomized: false, // <-- round type is from extended list
  simulationDurationType: "roundAmount",
  simulationRoundAmount: 10,
  simulationTimeStart: null, // set to current time
  simulationTimeEnd: null, // set to current time + 10 * round length

  targetValueColors: generateColors(5, {
    startColor: DEFAULT_ELEMENT_VALUE_COLOR_BAD,
    endColor: DEFAULT_ELEMENT_VALUE_COLOR_GOOD,
    middleColor: DEFAULT_ELEMENT_VALUE_COLOR_NEUTRAL
  }),
  customTargetValueAreaColors: false,
  developerMode: false,
  relationStrengthPresets: {
    weak: 10,
    medium: 30,
    strong: 50
  },

  areaOrder: ["model", "variables", "inspector", "simulation", "evaluation"],
  hiddenAreas: [],

  netGridSize: 10, // Todo: implement

  colorPrimary: null,
  colorPrimaryContrast: null, // auto-generated
  colorPrimaryHue: null,
  colorPrimaryTransparent: null, // auto-generated
  colorPrimaryLight: null, // auto-generated
  colorPrimaryDark: null, // auto-generated

  colorGrey: null, // auto-generated
  colorGreyDark: null, // auto-generated
  colorGreyDarker: null, // auto-generated
  colorGreyLight: null, // auto-generated
  colorGreyLighter: null, // auto-generated

  colorRelationStandard: null,
  colorRelationFaded: null,
  colorRelationActive: null,
  colorRelationActiveHue: null,
  colorRelationHighlighted: null,
  colorRelationIncoming: null,
  colorRelationOutgoing: null,
  colorRelationConnecting: null,

  extraInverseRelationColors: false,
  colorRelationInverse: null,
  colorRelationInverseActive: null,
  colorRelationInverseActiveHue: null,
  colorRelationInverseIncoming: null,
  colorRelationInverseOutgoing: null,
  colorRelationInverseConnecting: null,

  resourceItemsPosition: "net",

  netScaleBarVisible: true,
  netScaleIconsVisible: true,

  netActionsBarExtended: false,

  darkModeOverride: null // deactivate/activate dark mode in this model, regardless of the global setting
};

export const useSettingsStore = defineStore("model/settings", {
  state: () => cloneDeep(defaultSettings),

  getters: {
    targetValueAreaCount(state): number {
      return state.targetValueColors.length;
    },

    availableAreas(): { id: EditorAreaName }[] {
      return this.currentAreaOrder
        .filter((areaId: EditorAreaName) => canAreaBeDisplayed(areaId))
        .map((areaId) => ({ id: areaId }));
    },
    currentAreaOrder(state): EditorAreaName[] {
      if (!state.areaOrder) return [];

      // add areas that have not yet been added
      const newAreas = editorAreas.filter((a) => !state.areaOrder.includes(a));

      const updatedAreas = state.areaOrder.filter((areaName) =>
        editorAreas.includes(areaName)
      );
      return updatedAreas.concat(newAreas);
    },
    visibleAreas(state): { id: EditorAreaName }[] {
      return this.availableAreas.filter(
        (area: { id: EditorAreaName }) => !state.hiddenAreas.includes(area.id)
      );
    }
  },

  actions: {
    set<K extends keyof ModelSettings>(key: K, value: ModelSettings[K]) {
      this.$patch((state) => {
        state[key] = value;
      });
    },

    setTargetValueAreaCount(count: number) {
      console.log("setTargetValueAreaCount", count);
      if (this.customTargetValueAreaColors === false) {
        this.resetValueAreaColors(count);
      } else {
        this.changeTargetValueAreaCount(count);
      }
      return true;
    },
    reset() {
      this.$reset();
      this.resetValueAreaColors(STANDARD_COLOR_AMOUNT);
    },

    /**
     * Chooses if custom colors are used.
     * @param flag Should custom colors be used?
     */
    setCustomTargetValueAreas(flag: boolean) {
      this.customTargetValueAreaColors = flag;
    },

    setTargetValueAreaColor(areaOptions: { color: string; index: number }) {
      if (this.customTargetValueAreaColors === false) {
        this.setCustomTargetValueAreas(true);
      }
      const { color, index } = areaOptions;
      this.targetValueColors[index] = color;
    },
    /**
     *
     * @param count The amount of value areas there should be.
     */
    changeTargetValueAreaCount(count: number) {
      if (count < 1) {
        count = 1;
      }
      const middle = Math.ceil(count / 2);
      if (this.targetValueColors.length > count) {
        const diff = this.targetValueColors.length - count;
        console.log("remove", this.targetValueColors.length, count, -diff);
        const before = Math.ceil(diff / 2);
        const after = Math.floor(diff / 2);
        console.log("remove index", middle - before);
        this.targetValueColors = this.targetValueColors.slice(
          0, middle - before
        ).concat(
          this.targetValueColors.slice(
            middle + after, this.targetValueColors.length
          )
        );
        console.log("new targetValueColors", this.targetValueColors);
      } else if (this.targetValueColors.length < count) {
        const middle = Math.floor(count / 2);
        this.targetValueColors.splice(
          middle, 0, "#444"
        );

        // while (this.targetValueColors.length < count) {
        //   this.targetValueColors.push(#444);
        // }
      }
    },
    /**
     * Generate a set number of colors between red and green and assign them
     * to the target value areas.
     * @param count How many color steps should there be, including the extremes
     */
    resetValueAreaColors(count?: number) {
      if (count == null) {
        count = this.targetValueColors.length;
      }
      this.customTargetValueAreaColors = false;
      this.targetValueColors = generateColors(count, {
        startColor: DEFAULT_ELEMENT_VALUE_COLOR_BAD,
        endColor: DEFAULT_ELEMENT_VALUE_COLOR_GOOD,
        middleColor: DEFAULT_ELEMENT_VALUE_COLOR_NEUTRAL
      });
    },
    generateValueAreaColors() {
      this.targetValueColors = generateColors(
        this.targetValueColors.length,
        {
          startColor: this.targetValueColors[0],
          endColor: this.targetValueColors[this.targetValueColors.length - 1]
        }
      );
    },

    /**
     *
     */
    setColor(colorName: ColorName, value: string | null) {
      if (!value || value.startsWith("var")) {
        // don't save var()-values, they are the default anyway.
        this[colorName] = null;

        const hueName = `${colorName}Hue`;

        // if the color has an extra hue property, update that too.
        if (hueName in this) {
          this[hueName as ColorHueName] = null;
        }

        return null;
      }

      this[colorName] = value;

      const hueName = `${colorName}Hue`;

      // if the color has an extra hue property, update that too.
      if (hueName in this) {
        const color = Tinycolor(value);
        const hsl = color.toHsl();
        this[hueName as ColorHueName] = Math.round(hsl.h);
      }

      return value;
    },

    /**
     * @param value
     */
    setPrimaryColor(value: string | null) {
      value = this.setColor("colorPrimary", value);

      if (value == null) {
        this.colorPrimaryHue = null;
        this.colorPrimaryContrast = null;
        this.colorPrimaryTransparent = null;
        this.colorPrimaryLight = null;
        this.colorPrimaryDark = null;

        this.colorGrey = null;
        this.colorGreyDark = null;
        this.colorGreyDarker = null;
        this.colorGreyLight = null;
        this.colorGreyLighter = null;
      } else {
        const contrastColor = getContrastColor(value);
        this.setColor("colorPrimaryContrast", contrastColor);

        const baseColor = Tinycolor(value);

        const transparentColor = baseColor.clone();
        transparentColor.setAlpha(0.5);
        this.setColor(
          "colorPrimaryTransparent",
          transparentColor.toHslString()
        );

        this.regenerateColors([
          "colorPrimaryLight",
          "colorPrimaryDark",
          "colorGreyDarker",
          "colorGreyDark",
          "colorGrey",
          "colorGreyLight",
          "colorGreyLighter"
        ]);
      }
    },

    /**
     * @param colorNames
     */
    regenerateColors(colorNames: ColorName[]) {
      if (this.colorPrimary == null) {
        return colorNames.forEach((colorName) =>
          this.setColor(colorName, null)
        );
      }

      const baseColor = Tinycolor(this.colorPrimary);
      const baseHSL = baseColor.toHsl();

      for (const colorName of colorNames) {
        if (!(colorName in this)) {
          throw new Error("Color does not exist.");
        }

        switch (colorName) {
          case "colorPrimaryLight": {
            const lightColor = Tinycolor({
              h: baseHSL.h + 8,
              s: 0.70,
              l: 0.96
            });
            this.setColor("colorPrimaryLight", lightColor.toHslString());
            break;
          }

          case "colorPrimaryDark": {
            const darkColor = baseColor.clone();
            darkColor.darken(10);
            this.setColor("colorPrimaryDark", darkColor.toHslString());
            break;
          }

          case "colorGrey": {
            const grey = Tinycolor({ h: baseHSL.h + 14, s: 0.15, l: 0.91 });
            this.setColor("colorGrey", grey.toHslString());
            break;
          }

          case "colorGreyDark": {
            const greyDark = Tinycolor({ h: baseHSL.h + 11, s: 0.05, l: 0.72 });
            this.setColor("colorGreyDark", greyDark.toHslString());
            break;
          }

          case "colorGreyDarker": {
            const greyDarker = Tinycolor({
              h: baseHSL.h + 8,
              s: 0.05,
              l: 0.55
            });
            this.setColor("colorGreyDarker", greyDarker.toHslString());
            break;
          }

          case "colorGreyLight": {
            const greyLight = Tinycolor({ h: baseHSL.h + 8, s: 0.15, l: 0.93 });
            this.setColor("colorGreyLight", greyLight.toHslString());
            break;
          }

          case "colorGreyLighter": {
            const greyLighter = Tinycolor({ ...baseHSL, s: 0.15, l: 0.98 });
            this.setColor("colorGreyLighter", greyLighter.toHslString());
            break;
          }
        }
      }
    }
  }
});

export function getWritableSettings<A extends keyof ModelSettings>(
  settingsToGet: A[]
) {
  const settings = useSettingsStore();
  type WritableSettings = {
    [key in A]: WritableComputedRef<ModelSettings[key]>;
  };

  const writableSettings: Partial<WritableSettings> = {};

  settingsToGet.forEach(<K extends A>(key: K) => {
    writableSettings[key] = getWritableSetting<ModelSettings, K>(settings, key);
  });

  return writableSettings as WritableSettings;
}

function getWritableSetting<State extends StateTree, K extends keyof State>(
  settings: Store<string, State, object, object>,
  key: K
) {
  return computed({
    get() {
      return (settings as State)[key];
    },
    set(value: State[K]) {
      settings.$patch((state) => {
        (state as State)[key] = value;
      });
    }
  });
}

function generateColors(count: number, {
  endColor = DEFAULT_ELEMENT_VALUE_COLOR_GOOD,
  startColor = DEFAULT_ELEMENT_VALUE_COLOR_BAD,
  middleColor
}: { endColor: string, startColor: string, middleColor?: string } = { endColor: DEFAULT_ELEMENT_VALUE_COLOR_GOOD, startColor: DEFAULT_ELEMENT_VALUE_COLOR_BAD }) {
  console.warn("generateColors", count);
  if (count === 0) {
    return [];
  }
  if (count === 1) {
    return ["#444"];
  }

  const colorEnd = Tinycolor(endColor);
  const colorStart = Tinycolor(startColor);
  let colors = [];

  if (count === 2) {
    return ["#" + colorStart.toHex(), "#" + colorEnd.toHex()];
  }

  if (middleColor == null) {
    const startHSL = colorEnd.toHsl();
    const endHSL = colorStart.toHsl();
    const startHue = startHSL.h;
    const endHue = endHSL.h;
    const differenceRight = startHue - endHue;
    const differenceLeft = endHue - startHue;
    const difference = Math.abs(differenceRight) > Math.abs(differenceLeft)
      ? differenceRight
      : differenceLeft;
    const middle = difference / 2;
    const middleHSL = {
      h: startHSL.h + middle % 360,
      s: (startHSL.s + endHSL.s) / 2,
      l: (startHSL.l + endHSL.l) / 2
    };
    middleColor = Tinycolor(middleHSL).toHslString();
    console.log("middleColor", middleColor, startHue, endHue, difference);
  }

  const colorMiddle = Tinycolor(middleColor);

  const end = count - 1;
  const middle = end / 2;

  const colorsStart = [];
  const colorsEnd = [];

  for (let i = 0; i <= middle; i++) {
    const color = Tinycolor.mix(
      colorStart,
      colorMiddle,
      Math.round((i / middle) * 100)
    );
    colorsStart.push("#" + color.toHex());
  }
  for (let i = 0; i < middle; i++) {
    const color = Tinycolor.mix(
      colorEnd,
      colorMiddle,
      Math.round((i / middle) * 100)
    );
    colorsEnd.unshift("#" + color.toHex());
  }

  colors = colorsStart.concat(colorsEnd);
  console.log("colors", colors);
  return colors;
}

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useSettingsStore as any, import.meta.hot)
  );
}
